(() => {
  const galleryTransitions = new WeakMap();

  const galleryUrl = (value) => {
    try {
      return new URL(value, document.baseURI).href;
    } catch (error) {
      return value || '';
    }
  };

  const setActiveGalleryThumb = (root, thumb) => {
    if (!thumb?.matches?.('[data-vp-gallery-thumb]')) return;
    root.querySelectorAll('[data-vp-gallery-thumb]').forEach((item) => {
      const active = item === thumb;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const applyGalleryImage = (image, source, alt) => {
    image.src = source;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.alt = alt || image.alt;
  };

  const swapGalleryImage = (root, source) => {
    const image = root.querySelector('[data-vp-gallery-main]');
    const shell = image?.closest('.vp-pdp__gallery-main');
    const nextSrc = typeof source === 'string' ? source : source?.dataset?.src;
    const nextAlt = typeof source === 'string' ? image?.alt : source?.dataset?.alt || image?.alt;
    if (!image || !shell || !nextSrc) return;

    setActiveGalleryThumb(root, source);

    const targetUrl = galleryUrl(nextSrc);
    const currentTransition = galleryTransitions.get(shell);
    if (currentTransition?.targetUrl === targetUrl) return;

    const currentUrl = galleryUrl(image.currentSrc || image.src);
    if (!currentTransition && currentUrl === targetUrl) {
      image.alt = nextAlt || image.alt;
      return;
    }

    currentTransition?.cancel();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applyGalleryImage(image, nextSrc, nextAlt);
      return;
    }

    const loader = new window.Image();
    let overlay;
    let fallbackTimer;
    let started = false;
    let finished = false;
    let cancelled = false;

    const removeOverlay = () => {
      if (cancelled) return;
      overlay?.remove();
      shell.classList.remove('is-crossfading');
      if (galleryTransitions.get(shell) === transitionState) {
        galleryTransitions.delete(shell);
      }
    };

    const finish = () => {
      if (cancelled || finished) return;
      finished = true;
      window.clearTimeout(fallbackTimer);
      applyGalleryImage(image, nextSrc, nextAlt);

      const decoded = typeof image.decode === 'function'
        ? image.decode().catch(() => {})
        : Promise.resolve();
      decoded.then(() => window.requestAnimationFrame(removeOverlay));
    };

    const cancel = () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      overlay?.remove();
      shell.classList.remove('is-crossfading');
      if (galleryTransitions.get(shell) === transitionState) {
        galleryTransitions.delete(shell);
      }
    };

    const transitionState = { targetUrl, cancel };
    galleryTransitions.set(shell, transitionState);

    const start = () => {
      if (cancelled || started) return;
      started = true;

      overlay = image.cloneNode(false);
      overlay.className = 'vp-pdp__gallery-transition-image';
      overlay.src = nextSrc;
      overlay.alt = '';
      overlay.decoding = 'async';
      overlay.removeAttribute('data-vp-gallery-main');
      overlay.removeAttribute('srcset');
      overlay.removeAttribute('sizes');
      overlay.removeAttribute('fetchpriority');
      overlay.setAttribute('aria-hidden', 'true');

      shell.appendChild(overlay);
      shell.classList.add('is-crossfading');
      overlay.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'opacity') finish();
      });

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => overlay?.classList.add('is-visible'));
      });
      fallbackTimer = window.setTimeout(finish, 760);
    };

    const fail = () => {
      if (cancelled) return;
      applyGalleryImage(image, nextSrc, nextAlt);
      cancel();
    };

    loader.decoding = 'async';
    loader.addEventListener('load', start, { once: true });
    loader.addEventListener('error', fail, { once: true });
    loader.src = nextSrc;

    if (loader.complete) {
      if (loader.naturalWidth > 0) start();
      else fail();
    }
  };

  window.VibePinGallery = Object.assign(window.VibePinGallery || {}, {
    swap: swapGalleryImage
  });

  const initGallery = (scope = document) => {
    scope.querySelectorAll('[data-vp-pdp]').forEach((root) => {
      const gallery = root.querySelector('.vp-pdp__gallery');
      if (!gallery || gallery.dataset.vpEnhanced === 'true') return;
      gallery.dataset.vpEnhanced = 'true';
      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-vp-gallery-thumb]');
        if (!thumb || !gallery.contains(thumb)) return;
        swapGalleryImage(root, thumb);
      }, true);
    });
  };

  let reviewMotionObserver;
  const initReviewCarousels = (scope = document) => {
    scope.querySelectorAll('.vp-pdp__reviews-viewport').forEach((viewport) => {
      if (viewport.dataset.vpReviewMotion === 'true') return;
      viewport.dataset.vpReviewMotion = 'true';

      const track = viewport.querySelector('.vp-pdp__review-track');
      if (!track || viewport.classList.contains('vp-pdp__reviews-viewport--editor') || viewport.classList.contains('vp-pdp__reviews-viewport--single')) return;

      if (!('IntersectionObserver' in window)) return;
      track.style.setProperty('animation-play-state', 'paused', 'important');
      reviewMotionObserver ||= new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const reviewTrack = entry.target.querySelector('.vp-pdp__review-track');
          if (!reviewTrack) return;
          reviewTrack.style.setProperty('animation-play-state', entry.isIntersecting ? 'running' : 'paused', 'important');
        });
      }, { rootMargin: '120px 0px', threshold: .01 });
      reviewMotionObserver.observe(viewport);
    });
  };

  const initReviewLightboxes = (scope = document) => {
    scope.querySelectorAll('.vp-pdp__reviews').forEach((section) => {
      const lightbox = section.querySelector('[data-vp-review-lightbox]');
      const image = lightbox?.querySelector('[data-vp-review-lightbox-image]');
      const caption = lightbox?.querySelector('[data-vp-review-lightbox-caption]');
      const closeButton = lightbox?.querySelector('[data-vp-review-lightbox-close]');
      if (!lightbox || !image || !caption || !closeButton || lightbox.dataset.vpReviewLightboxReady === 'true') return;
      lightbox.dataset.vpReviewLightboxReady = 'true';

      let trigger;
      let closeTimer = 0;

      const onKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeLightbox();
        }

        if (event.key === 'Tab') {
          event.preventDefault();
          closeButton.focus();
        }
      };

      const closeLightbox = ({ restoreFocus = true } = {}) => {
        if (lightbox.hidden) return;
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('vp-pdp-review-lightbox-open');
        document.removeEventListener('keydown', onKeydown);

        const previousTrigger = trigger;
        trigger = undefined;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          lightbox.hidden = true;
          image.removeAttribute('src');
          image.alt = '';
          caption.textContent = '';
          if (restoreFocus && previousTrigger?.isConnected) previousTrigger.focus({ preventScroll: true });
        }, 230);
      };

      const openLightbox = (button) => {
        const source = button.dataset.vpReviewPhotoSrc;
        if (!source) return;

        window.clearTimeout(closeTimer);
        trigger = button;
        image.src = source;
        image.alt = button.dataset.vpReviewPhotoAlt || 'Zdjęcie VibePina dodane przez klienta';
        caption.textContent = button.dataset.vpReviewPhotoCaption || '';
        caption.hidden = !caption.textContent;
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('vp-pdp-review-lightbox-open');
        document.addEventListener('keydown', onKeydown);

        window.requestAnimationFrame(() => {
          lightbox.classList.add('is-open');
          closeButton.focus({ preventScroll: true });
        });
      };

      section.addEventListener('click', (event) => {
        const photoButton = event.target.closest('[data-vp-review-photo]');
        if (photoButton && section.contains(photoButton)) openLightbox(photoButton);
      });

      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target.closest('[data-vp-review-lightbox-close]')) closeLightbox();
      });

      section.addEventListener('shopify:block:select', () => closeLightbox({ restoreFocus: false }));
    });
  };

  let storyVideoObserver;
  const storyVideoStates = new WeakMap();

  const lockStoryVideo = (video) => {
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;
    video.tabIndex = -1;

    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
    video.setAttribute('aria-hidden', 'true');
    video.removeAttribute('controls');
  };

  const playStoryVideo = (video) => {
    const state = storyVideoStates.get(video);
    if (!state?.shouldPlay || document.hidden) return;

    window.clearTimeout(state.retryTimer);

    const playPromise = video.play();
    if (playPromise?.then) {
      playPromise.then(() => {
        state.retryCount = 0;
      }).catch(() => {
        if (!state.shouldPlay || document.hidden) return;
        if (state.retryCount >= 4) return;
        state.retryCount += 1;
        state.retryTimer = window.setTimeout(() => playStoryVideo(video), 650);
      });
    }
  };

  const pauseStoryVideo = (video) => {
    const state = storyVideoStates.get(video);
    if (state) window.clearTimeout(state.retryTimer);
    if (!video.paused) video.pause();
  };

  const initStoryVideos = (scope = document) => {
    const videos = scope.querySelectorAll('.vp-pdp__video-media video');
    if (!videos.length) return;

    if ('IntersectionObserver' in window && !storyVideoObserver) {
      storyVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const state = storyVideoStates.get(video);
          if (!state) return;
          state.shouldPlay = entry.isIntersecting;
          if (state.shouldPlay) playStoryVideo(video);
          else pauseStoryVideo(video);
        });
      }, { rootMargin: '160px 0px', threshold: .01 });
    }

    videos.forEach((video) => {
      if (video.dataset.vpAutoplayReady === 'true') return;
      video.dataset.vpAutoplayReady = 'true';
      storyVideoStates.set(video, { shouldPlay: false, retryTimer: 0, retryCount: 0 });
      lockStoryVideo(video);

      video.addEventListener('canplay', () => playStoryVideo(video), { once: true, passive: true });

      if (storyVideoObserver) storyVideoObserver.observe(video);
      else {
        storyVideoStates.get(video).shouldPlay = true;
        playStoryVideo(video);
      }
    });
  };

  const resumeVisibleStoryVideos = () => {
    document.querySelectorAll('.vp-pdp__video-media video').forEach((video) => {
      const state = storyVideoStates.get(video);
      if (!state) return;
      if (document.hidden) pauseStoryVideo(video);
      else if (state.shouldPlay) playStoryVideo(video);
    });
  };

  const initUseCases = (scope = document) => {
    scope.querySelectorAll('[data-vp-usecase]').forEach((root) => {
      if (root.dataset.vpUsecaseReady === 'true') return;
      root.dataset.vpUsecaseReady = 'true';

      const tabs = Array.from(root.querySelectorAll('[data-vp-usecase-tab]'));
      const panels = Array.from(root.querySelectorAll('[data-vp-usecase-panel]'));
      const interval = Math.max(4000, Number(root.dataset.vpAutoplay) || 6000);
      let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains('is-active')));
      let timer;
      let transitionTimer;
      let transitionToken = 0;
      let isVisible = !('IntersectionObserver' in window);

      if (!tabs.length || tabs.length !== panels.length) return;

      const syncVideos = () => {
        panels.forEach((panel, index) => {
          panel.querySelectorAll('video').forEach((video) => {
            if (isVisible && !document.hidden && index === activeIndex) {
              video.muted = true;
              const playPromise = video.play();
              if (playPromise?.catch) playPromise.catch(() => {});
            } else {
              video.pause();
            }
          });
        });
      };

      const schedule = () => {
        window.clearTimeout(timer);
        if (document.hidden || !isVisible) return;
        timer = window.setTimeout(() => {
          activate((activeIndex + 1) % tabs.length);
        }, interval);
      };

      const syncPanelAccessibility = () => {
        panels.forEach((panel, index) => {
          const isActive = index === activeIndex;
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (isActive) panel.removeAttribute('inert');
          else panel.setAttribute('inert', '');
        });
      };

      const crossfade = (previousPanel, nextPanel) => {
        const token = ++transitionToken;
        window.clearTimeout(transitionTimer);

        panels.forEach((panel) => {
          if (panel !== previousPanel && panel !== nextPanel) {
            panel.classList.remove('is-active', 'is-leaving');
          }
        });

        if (previousPanel && previousPanel !== nextPanel) {
          previousPanel.classList.add('is-leaving');
          previousPanel.classList.remove('is-active');
        }

        nextPanel.classList.remove('is-leaving');
        nextPanel.classList.add('is-active');

        const finish = () => {
          if (token !== transitionToken) return;
          transitionTimer = undefined;
          panels.forEach((panel) => {
            panel.classList.remove('is-leaving');
            if (panel !== nextPanel) panel.classList.remove('is-active');
          });
          nextPanel.classList.add('is-active');
        };

        if (previousPanel === nextPanel) {
          finish();
          return;
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          finish();
          return;
        }

        const onTransitionEnd = (event) => {
          if (event.propertyName === 'opacity') finish();
        };
        nextPanel.addEventListener('transitionend', onTransitionEnd, { once: true });
        transitionTimer = window.setTimeout(finish, 920);
      };

      const activate = (nextIndex, shouldFocus = false) => {
        if (nextIndex < 0 || nextIndex >= tabs.length) return;
        if (nextIndex === activeIndex) {
          if (shouldFocus) tabs[activeIndex].focus();
          schedule();
          return;
        }

        const previousPanel = panels.find((panel) => panel.classList.contains('is-active')) || panels[activeIndex];
        const nextPanel = panels[nextIndex];
        activeIndex = nextIndex;

        tabs.forEach((tab) => {
          tab.classList.remove('is-active');
          tab.setAttribute('aria-selected', 'false');
          tab.tabIndex = -1;
        });

        syncPanelAccessibility();

        tabs[activeIndex].classList.add('is-active');
        tabs[activeIndex].setAttribute('aria-selected', 'true');
        tabs[activeIndex].tabIndex = 0;
        crossfade(previousPanel, nextPanel);

        if (shouldFocus) tabs[activeIndex].focus();
        syncVideos();
        schedule();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          isVisible = true;
          root.classList.add('is-in-viewport');
          activate(index);
        });
        tab.addEventListener('keydown', (event) => {
          let nextIndex;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === undefined) return;
          event.preventDefault();
          activate(nextIndex, true);
        });
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          window.clearTimeout(timer);
          syncVideos();
        } else if (isVisible) {
          syncVideos();
          schedule();
        }
      });

      syncPanelAccessibility();
      syncVideos();
      schedule();

      if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver((entries) => {
          isVisible = entries[0]?.isIntersecting || false;
          root.classList.toggle('is-in-viewport', isVisible);
          if (isVisible) {
            syncVideos();
            schedule();
          } else {
            window.clearTimeout(timer);
            syncVideos();
          }
        }, { rootMargin: '160px 0px', threshold: .01 });
        visibilityObserver.observe(root);
      } else {
        root.classList.add('is-in-viewport');
      }
    });
  };

  let revealObserver;
  const initReveal = (scope = document) => {
    const elements = scope.querySelectorAll('[data-vp-reveal]');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    document.documentElement.classList.add('vp-motion');
    revealObserver ||= new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .12 });

    elements.forEach((element) => {
      if (!element.classList.contains('is-visible')) revealObserver.observe(element);
    });
  };

  const initDeferredEnhancements = (scope = document) => {
    initReviewCarousels(scope);
    initReviewLightboxes(scope);
    initStoryVideos(scope);
    initUseCases(scope);
  };

  const init = (scope = document) => {
    initGallery(scope);
    initReveal(scope);

    const runDeferred = () => initDeferredEnhancements(scope);

    if (scope === document && 'requestIdleCallback' in window) {
      window.requestIdleCallback(runDeferred, { timeout: 1200 });
    } else if (scope === document) {
      window.setTimeout(runDeferred, 120);
    } else {
      initDeferredEnhancements(scope);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('visibilitychange', resumeVisibleStoryVideos);
  window.addEventListener('pageshow', resumeVisibleStoryVideos, { passive: true });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
